import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/app/Shell";
import RequestCard from "@/components/app/RequestCard";
import NewRequestDialog from "@/components/app/NewRequestDialog";
import RatingDialog from "@/components/app/RatingDialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/apiClient";
import { waContactLink, PAYMENT_METHODS_CUSTOMER } from "@/lib/status";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CustomerEquipmentTab from "@/components/app/CustomerEquipmentTab";
import LoyaltyTab from "@/components/app/LoyaltyTab";
import CustomerHome from "@/components/app/CustomerHome";
import MobileTabBar from "@/components/app/MobileTabBar";
import ProfileEditDialog from "@/components/app/ProfileEditDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SUPPORT_WHATSAPP } from "@/lib/config";
import { useAuth } from "@/context/AuthContext";
import { Plus, ClipboardList, Star, XCircle, Pencil, AlertTriangle, MessageCircle, Home, Headphones, User, Snowflake, Award, Mail, Phone, IdCard } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_PHONE = "5521966242803";

function CustomerActions({ request, onChanged, onRate, onEdit }) {
  const cancel = async () => {
    try {
      await api.patch(`/requests/${request.id}/cancel`, { reason: "Cancelado pelo cliente" });
      toast.success("Chamado cancelado.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const canCancel = ["aberto", "atribuido"].includes(request.status);
  const canRate = request.status === "concluido" && !request.rating;
  const canEdit = request.status === "aberto";

  if (!canCancel && !canRate && !canEdit) return null;

  return (
    <div className="flex gap-2 flex-wrap">
      {canRate && (
        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 gap-1.5"
          onClick={() => onRate(request)} data-testid={`rate-button-${request.id}`}>
          <Star className="h-4 w-4" /> Avaliar atendimento
        </Button>
      )}
      {canEdit && (
        <Button size="sm" variant="outline" className="gap-1.5"
          onClick={() => onEdit(request)} data-testid={`edit-request-${request.id}`}>
          <Pencil className="h-4 w-4" /> Editar
        </Button>
      )}
      {canCancel && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
              data-testid={`cancel-button-${request.id}`}>
              <XCircle className="h-4 w-4" /> Cancelar
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="cancel-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar chamado?</AlertDialogTitle>
              <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-dialog-no">Voltar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={cancel}
                data-testid="cancel-dialog-yes">Sim, cancelar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [ratingFor, setRatingFor] = useState(null);
  const [editReq, setEditReq] = useState(null);
  const [tab, setTab] = useState("inicio");
  const [preset, setPreset] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/requests");
      setRequests(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = (p = null) => { setPreset(p); setDialogOpen(true); };

  const handleTile = (key) => {
    switch (key) {
      case "assistencia": return openNew({ service_type: "Conserto" });
      case "enviar": return openNew({ service_type: "Conserto" });
      case "instalacao": return openNew({ service_type: "Instalação", equipment: "Ar-condicionado", is_quote: true });
      case "manutencao": return openNew({ service_type: "Manutenção" });
      case "higienizacao": return openNew({ service_type: "Higienização", equipment: "Ar-condicionado" });
      case "linhabranca": return openNew({ service_type: "Conserto", equipment: "Geladeira / Freezer" });
      case "comercial": return openNew({ equipment: "Refrigeração comercial" });
      case "orcamento": return openNew({ service_type: "Orçamento", is_quote: true });
      case "emergencia": return setEmergencyOpen(true);
      case "agendamentos": return setTab("chamados");
      case "avaliar": return setTab("chamados");
      case "especialista":
        return window.open(waContactLink(SUPPORT_PHONE, "Olá! Quero falar com um especialista da Clima Branca."), "_blank");
      case "franqueado":
        return window.open(window.location.origin, "_blank");
      default: return null;
    }
  };

  return (
    <Shell
      title="Área do cliente"
      subtitle="Abra chamados, acompanhe o técnico e gerencie seus equipamentos"
      actions={
        <div className="flex gap-2">
          <a href={waContactLink(SUPPORT_PHONE, "Olá! Preciso falar com a assistência da Clima Branca.")}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium text-green-700 border border-green-200 bg-green-50 hover:bg-green-100"
            data-testid="support-whatsapp-button">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <Button variant="outline" className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => setEmergencyOpen(true)} data-testid="emergency-request-button">
            <AlertTriangle className="h-4 w-4" /> Emergência
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={() => openNew(null)}
            data-testid="new-request-button">
            <Plus className="h-4 w-4" /> Novo chamado
          </Button>
        </div>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="inicio" data-testid="cust-tab-inicio">Início</TabsTrigger>
          <TabsTrigger value="chamados" data-testid="cust-tab-chamados">Meus chamados</TabsTrigger>
          <TabsTrigger value="equipamentos" data-testid="cust-tab-equipamentos">Meus equipamentos</TabsTrigger>
          <TabsTrigger value="fidelidade" data-testid="cust-tab-fidelidade">Fidelidade</TabsTrigger>
        </TabsList>

        <TabsContent value="inicio">
          <CustomerHome onAction={handleTile} userName={user?.name} requests={requests} onSeeAll={() => setTab("chamados")} onNav={setTab} />
        </TabsContent>

        <TabsContent value="chamados">
          {loading ? (
            <p className="text-slate-400 text-sm">Carregando...</p>
          ) : requests.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-state">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Você ainda não abriu nenhum chamado.</p>
              <Button className="mt-4 bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={() => openNew(null)}>
                <Plus className="h-4 w-4" /> Abrir meu primeiro chamado
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="requests-list">
              {requests.map((r) => (
                <RequestCard key={r.id} request={r} canPayOnline customerView paymentOptions={PAYMENT_METHODS_CUSTOMER} onChanged={load}
                  actions={<CustomerActions request={r} onChanged={load} onRate={setRatingFor} onEdit={setEditReq} />} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="equipamentos">
          <CustomerEquipmentTab />
        </TabsContent>

        <TabsContent value="fidelidade">
          <LoyaltyTab />
        </TabsContent>
      </Tabs>

      <NewRequestDialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setPreset(null); }}
        onCreated={() => { load(); setTab("chamados"); }} preset={preset} />
      <NewRequestDialog open={emergencyOpen} onOpenChange={setEmergencyOpen} onCreated={load} defaultEmergency />
      <NewRequestDialog open={!!editReq} onOpenChange={(o) => !o && setEditReq(null)}
        onCreated={load} editRequest={editReq} />
      <RatingDialog open={!!ratingFor} onOpenChange={(o) => !o && setRatingFor(null)}
        request={ratingFor || {}} onRated={load} />

      <ProfileEditDialog open={profileOpen} onOpenChange={setProfileOpen} />

      <MobileTabBar items={[
        { key: "inicio", label: "Início", icon: Home, onClick: () => setTab("inicio"), active: tab === "inicio" },
        { key: "chamados", label: "Chamados", icon: ClipboardList, onClick: () => setTab("chamados"), active: tab === "chamados" },
        { type: "fab", label: "Solicitar", icon: Plus, onClick: () => openNew(null) },
        { key: "suporte", label: "Suporte", icon: Headphones, onClick: () => window.open(`https://wa.me/${SUPPORT_WHATSAPP.replace(/\D/g, "")}?text=${encodeURIComponent("Olá! Preciso de suporte da Clima Branca.")}`, "_blank") },
        { type: "menu", key: "perfil", label: "Perfil", icon: User, links: [
          { key: "dados", label: "Meus dados", icon: User, onClick: () => setProfileOpen(true) },
          { key: "equip", label: "Meus equipamentos", icon: Snowflake, onClick: () => setTab("equipamentos") },
          { key: "fidelidade", label: "Fidelidade", icon: Award, onClick: () => setTab("fidelidade") },
        ] },
      ]} />
    </Shell>
  );
}
